import { Checkbox, Flex, Icon, READABLE_LINE_MAX_WIDTH, Text, TextField, Button, createMarkup, Rte, useModal, INNER_MAX_WIDTH } from '@capioweb/ui-components/src'
import { BooleanProperty, LinkProperty, StringProperty } from '../../@types/EpiFoundationSpa/Property'
import React from 'react'
import { LocalesContext } from '../../context'
import { SharedFormProps } from '../../@types/CapioWeb/core'
import { useForm } from 'react-hook-form'
import { postAccountForm } from '../MyPages/api/postAccountForm'
import { InfoModal } from '../../pages/MyPagesUserRegistrationPage/InfoModal'
import { StyledButtonContent, StyledIcon } from '../MyPagesUserLifestyleFormBlock/LifestyleForm'
import { getAntiforgeryToken } from '../../utils/antiforgery'
import { isLocalStorageAvailable } from '../../utils'

interface ConsentsBlockProps {
    heading: StringProperty
    body: StringProperty
    consentForm: SharedFormProps
    topMargin: BooleanProperty
    leftAligned?: BooleanProperty
}

export const ConsentsBlock: React.FC<ConsentsBlockProps> = ({ heading, body, topMargin, leftAligned, consentForm }) => {
    const { translate, translationKeys } = React.useContext(LocalesContext)
    const [loading, setLoading] = React.useState<boolean>(false)
    const [hideBlock, setHideBlock] = React.useState<boolean>(false)


    // Register form
    const {
        register,
        handleSubmit,
        formState: { errors },
    } = useForm({
        mode: "onChange",
    })

    // Submit handler
    const onSubmit = async (data) => {

        setLoading(true)

        const { action } = consentForm
        const antiForgeryToken = await getAntiforgeryToken()
        try {
            const result = await postAccountForm(
                action,
                antiForgeryToken,
                data,
                consentForm.method
            )

            if (result.success) {
                setFeedbackMsg({
                    success: true,
                    message: translate(
                        translationKeys.manageAccountFeedbackMessageSuccess
                    ),
                })
            } else {
                setFeedbackMsg({
                    success: false,
                    message: result.data?.message ?? "Något gick fel",
                })
            }
        } catch (e) {
            console.error("---Catch", e, "--- error")
        }

        setLoading(false)

    }

    // Get props for submit button
    const submitButtonProps = consentForm.fields.find(
        (field) => field.type === "submit"
    )

    // Feedback message
    const [feedbackMsg, setFeedbackMsg] =
        React.useState<{
            success: boolean
            message: string
        }>(null)

    // Modal hook
    const { addModal } = useModal()


    React.useEffect(() => {
        if (isLocalStorageAvailable()) {
            if (localStorage.getItem('hideConsentsBlock')) setHideBlock(true)
        }

    })

    if (hideBlock) return null


    return (
        <Flex as="section" w={1} px={{ _: 0, m: 3 }} pb={3} mt={topMargin.value ? 3 : 0}>
            <Flex maxw={INNER_MAX_WIDTH} w={1} mx='auto'>
                <Flex
                    maxw={READABLE_LINE_MAX_WIDTH}
                    w={1}
                    m={leftAligned.value ? 0 : "0 auto"}
                    borderRadius={{ _: 0, m: 8 }}
                    backgroundColor="blue66"
                    position='relative'
                >
                    <Flex
                        as='button'
                        position="absolute"
                        top="32px"
                        right="32px"
                        onClick={() => {
                            if (isLocalStorageAvailable()) localStorage.setItem('hideConsentsBlock', '1')
                            setHideBlock(true)
                        }
                        }
                    >
                        <Icon icon="close"></Icon>
                    </Flex>
                    <Flex
                        w={1}
                        p={4}
                        justifyContent="center"
                    >
                        <Flex
                            flexGrow={1}
                            flexDirection="column"

                        >

                            {heading.value && (
                                <Text
                                    variant="headline-2"
                                    mb={2}

                                >
                                    {heading.value}
                                </Text>
                            )}
                            {body.value && (
                                <Rte>
                                    <div dangerouslySetInnerHTML={createMarkup(body.value)}></div>
                                </Rte>
                            )}


                            <Flex
                                as="form"
                                action={consentForm.action}
                                encType="application/x-www-form-urlencoded"
                                method={consentForm.method}
                                onSubmit={handleSubmit(onSubmit)}
                                flexDirection="column"
                            >
                                {consentForm.fields.map((field) => {
                                    const isInputField = !["submit"].includes(field.type)
                                    const requiredFields = [
                                        "terms",
                                    ]

                                    if (field && field.type && isInputField) {
                                        const { type, value, required, ...rest } = field
                                        const { ref, ...registerProps } = register(field.name, {
                                            required: {
                                                value:
                                                    requiredFields.includes(field.name) && !field.disabled
                                                        ? true
                                                        : false,
                                                message:
                                                    consentForm.requiredMessage !== ""
                                                        ? consentForm.requiredMessage
                                                        : translate(translationKeys.requiredFieldMessage),
                                            },
                                            ...(field.validators?.length > 0 && {
                                                pattern: {
                                                    value: new RegExp(field.validators[0].pattern),
                                                    message: field.validators[0].message,
                                                },
                                            }),
                                        })
                                        const fieldProps = {
                                            ...registerProps,
                                            ...rest,
                                            type,
                                            key: field.id,
                                            error: errors[field.name],
                                            ...(field.type !== "hidden" &&
                                                errors[field.name] && {
                                                helperText: errors[field.name].message,
                                            }),
                                        }

                                        if (["text", "email", "tel"].includes(type)) {
                                            return (
                                                <TextField inputRef={ref} defaultValue={value} {...fieldProps} />
                                            )
                                        } else {
                                            return {
                                                checkbox: (
                                                    <Flex key={field.id} mb={2} justifyContent="space-between">
                                                        <Checkbox
                                                            inputRef={ref}
                                                            value={
                                                                field.items?.length > 0
                                                                    ? field.items[0].value
                                                                    : field.label
                                                            }
                                                            defaultChecked={
                                                                field.items?.length > 0 && field.items[0].checked
                                                            }
                                                            {...fieldProps}
                                                        />
                                                        {consentForm.modals?.find((m) => m.id === field.id) && (
                                                            <Flex
                                                                as="button"
                                                                aria-label={`${translate(translationKeys.openModalFor)} ${field.label
                                                                    }`}
                                                                flexShrink={0}
                                                                onClick={(e) => {
                                                                    e.preventDefault()
                                                                    addModal(
                                                                        <InfoModal
                                                                            lastFocusedElement={e.target}
                                                                            {...consentForm.modals.find((m) => m.id === field.id)}
                                                                        />
                                                                    )
                                                                }}
                                                            >
                                                                <Icon
                                                                    icon="info-circle"
                                                                    size={3}
                                                                // style={{ pointerEvents: "none" }}
                                                                />
                                                            </Flex>
                                                        )}
                                                    </Flex>
                                                ),
                                                hidden: (
                                                    <input
                                                        ref={ref}
                                                        readOnly
                                                        key={field.value}
                                                        value={field.value}
                                                        {...fieldProps}
                                                    />
                                                ),
                                            }[type]
                                        }
                                    }
                                })}
                                {submitButtonProps && (
                                    <Flex flexDirection="column" alignItems="flex-start">
                                        <Button mt={3} ml="auto" style={{ position: 'relative' }} type={loading ? 'button' : "submit"}>
                                            <StyledIcon aria-hidden loading={loading}>
                                                <Icon icon="ellipse" color='white' />
                                            </StyledIcon>
                                            <StyledButtonContent loading={loading}>{translate(translationKeys.save)}</StyledButtonContent>
                                        </Button>
                                        {/* Feedback message - used if we don't redirect (errors & success) */}
                                        <Flex
                                            h={feedbackMsg ? "auto" : "0"}
                                            overflow="hidden"
                                            w={1}
                                            mt={feedbackMsg ? 1.5 : 0}
                                            justifyContent='end'
                                            role="region"
                                            aria-live="polite"
                                        >
                                            <Text
                                                as="span"
                                                textColor={feedbackMsg?.success ? "primary" : "alert"}
                                            >
                                                {feedbackMsg?.message}
                                            </Text>
                                        </Flex>
                                    </Flex>
                                )}
                            </Flex>
                        </Flex>
                    </Flex>
                </Flex>
            </Flex>
        </Flex >
    )
}
